import { Box } from "@mui/material";
import PropTypes from "prop-types";
import { colorsFoundation } from "../tokens/colors-foundation.js";

// Solid saturated hues (the click-dummy's avatar `col` cycle) — rendered with
// white initials on top. A label is hashed to a stable colour so the same entity
// always gets the same one.
const AVATAR_COLORS = [
  "#e11d48", // rose
  "#3b82f6", // blue
  "#8b5cf6", // violet
  "#10b981", // emerald
  "#f59e0b", // amber
  "#0ea5e9", // sky
  "#db2777", // pink
  "#14b8a6", // teal
  "#a855f7", // purple
  "#f97316", // orange
];

function hashIndex(str, len) {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = str.charCodeAt(i) + ((h << 5) - h);
  }
  return Math.abs(h) % len;
}

function getInitials(label) {
  const parts = String(label || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * EntityAvatar — the click-dummy's leading-column avatar. Renders, in priority:
 *   1. an image (`src`),
 *   2. a symbol/icon (`icon`) on a tinted background,
 *   3. initials derived from `label` on a stable hashed colour.
 * `variant` is "rounded" (rounded square, default — used for entities like
 * companies/objects) or "circular" (used for people). `color` overrides the hue.
 */
export default function EntityAvatar({
  label = "",
  src,
  icon,
  size = 34,
  variant = "rounded",
  color,
}) {
  const radius = variant === "circular" ? "50%" : "9px";

  const base = {
    width: size,
    height: size,
    flex: "none",
    borderRadius: radius,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  };

  if (src) {
    return (
      <Box sx={base}>
        <Box
          component="img"
          src={src}
          alt={label}
          sx={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      </Box>
    );
  }

  // Dummy: solid saturated background with white content. Both icon and initials
  // avatars share the same hue logic — an explicit `color`, else a stable hue
  // hashed from `label` (falling back to blue when there's nothing to hash).
  const bg =
    color ??
    (label
      ? AVATAR_COLORS[hashIndex(String(label), AVATAR_COLORS.length)]
      : colorsFoundation.key.blue[600]);

  if (icon) {
    return (
      <Box
        sx={{
          ...base,
          backgroundColor: bg,
          color: colorsFoundation.neutral.white,
          "& .MuiSvgIcon-root": { fontSize: size * 0.55 },
        }}
      >
        {icon}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        ...base,
        backgroundColor: bg,
        color: colorsFoundation.neutral.white,
        fontSize: size * 0.4,
        fontWeight: 700,
        lineHeight: 1,
      }}
    >
      {getInitials(label)}
    </Box>
  );
}

EntityAvatar.propTypes = {
  label: PropTypes.string,
  src: PropTypes.string,
  icon: PropTypes.node,
  size: PropTypes.number,
  variant: PropTypes.oneOf(["rounded", "circular"]),
  color: PropTypes.string,
};
